/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, lazy, useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import CategoryRefinements from '..';
import { useClearAllFilters } from '../use-clear-all-filters';
import { usePanelState, useIsNarrow } from './use-panel-presentation';

const FiltersOverlay = lazy(() => import('./overlay'));

/**
 * The filter panel's responsive shell. CRCB2B-348.
 *
 * Owns the one decision neither route should make twice: whether the groups appear as a desktop
 * sidebar or inside an overlay. `_app.category.$categoryId.tsx` and `_app.search.tsx` render the
 * same panel, so the split lives here or it diverges.
 *
 * ## The default is CSS, the overlay is JS
 *
 * The design shows the sidebar **open** at desktop and the panel closed below it. That default
 * cannot be expressed in state: `initialFiltersOpen` is computed in the loader from the URL, where
 * the viewport is unknown, so a breakpoint-aware JS default would either mismatch on hydration or
 * force a client-only render — a guaranteed layout shift on the widest viewport, which is the
 * opposite of what a default-open sidebar is for.
 *
 * So the query parameter grows a third state, **absent**, and the breakpoint resolves it in CSS:
 *
 * | `?filters=` | below `lg` | `lg`+ |
 * |---|---|---|
 * | absent | closed | **open** |
 * | `open` | overlay open | open |
 * | `closed` | closed | closed |
 *
 * The parameter still means the same thing at both widths — "the shopper's explicit choice" — only
 * the default differs. `getInitialFiltersOpen`, `useFiltersPanelState` and the revalidation helper
 * are untouched; this component reinterprets absence at the presentation layer.
 *
 * ## Why the overlay may use a media query when the sidebar may not
 *
 * The sidebar is server-rendered, so it must be CSS-gated. The overlay is `React.lazy()` and is
 * mounted only after the first interaction with the filter control — it is client-only by
 * construction, so `matchMedia` is safe there and carries no hydration risk. That gate is needed
 * because a dialog's open state cannot be resolved in CSS: without it, collapsing the sidebar at
 * desktop (`?filters=open` is the desktop default) would also pop an overlay.
 */
export default function CategoryRefinementsPanel({
    result,
    refine,
    filtersOpen,
    onFiltersOpenChange,
    className,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    /** The shopper's explicit choice, as read from the URL by `useFiltersPanelState`. */
    filtersOpen: boolean;
    /** Records a new explicit choice. Reuses the route's existing toggle. */
    onFiltersOpenChange: (open: boolean) => void;
    /**
     * Grid placement from the caller. The panel takes **no width of its own** — the route's page
     * grid sizes it through its `1fr` track, which is where the design's 336px comes from
     * (CRCB2B-87). A width here would fight that track.
     */
    className?: string;
}): ReactElement {
    const { t } = useTranslation();
    const panelState = usePanelState();
    const isNarrow = useIsNarrow();

    /**
     * The overlay's subtree is kept out of the document until the shopper first reaches for it,
     * per the project's lazy-overlay rule. It also keeps the second copy of the group markup off
     * the first paint — see `idPrefix` for why two copies are safe once it does mount.
     */
    const [overlayRequested, setOverlayRequested] = useState(false);
    useEffect(() => {
        if (filtersOpen) setOverlayRequested(true);
    }, [filtersOpen]);

    /** Shared with the applied-filter row and the over-filtered message — see the hook's note. */
    const clearAllFilters = useClearAllFilters();

    const overlayOpen = filtersOpen && isNarrow;

    return (
        <>
            <aside
                data-slot="filters-sidebar"
                data-filters={panelState}
                // Translated, not a literal: this is the landmark's accessible name, and the
                // storefront ships 17 locales. `filtersButtonLabel` is the same word the Filters
                // control announces, so the landmark and the control that reveals it match.
                aria-label={t('categoryRefinements:filtersButtonLabel')}
                // Never a sidebar below `lg` — that width gets the overlay. At `lg`+ it shows
                // unless the shopper explicitly closed it. The `data-` variant carries an extra
                // attribute selector, so it outranks `lg:block` without `!important`.
                //
                // No width: the route's page grid sizes this through its `1fr` track, which is
                // where the design's 336px comes from (CRCB2B-87). Placement arrives as
                // `className` for the same reason — the panel is a direct grid child, and only
                // the route knows the track names.
                className={cn('hidden px-4 lg:block lg:data-[filters=closed]:hidden', className)}>
                <CategoryRefinements result={result} refine={refine} idPrefix="sidebar" />
            </aside>

            {overlayRequested && (
                <Suspense fallback={null}>
                    <FiltersOverlay open={overlayOpen} onOpenChange={onFiltersOpenChange} onClearAll={clearAllFilters}>
                        <CategoryRefinements result={result} refine={refine} idPrefix="overlay" />
                    </FiltersOverlay>
                </Suspense>
            )}
        </>
    );
}
